import "./style.scss";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import classNames from "classnames";
import ModalBox from "components/Modal";
import ChannelIcon from "components/shared/ChannelIcon";
import LargeSwitch from "components/shared/LargeSwitch";
import ChannelPriceHistory from "components/Title/PriceHistory/ChannelPriceHistory";
import AddSpecificChannel from "components/TitleFormContainer/AddSpecificChannel";
import type { TFormCreateTitle } from "components/TitleFormContainer/types";
import { hasLegacyChannelName, hasLegacyEan13, hasLegacyReleaseDate } from "components/TitleFormContainer/utils";
import { featureFlags } from "config/featureFlags";
import type { ITitle } from "containers/Titles/types";
import { useCompanySettings } from "hooks/useCompanySettings";
import type { SetStateAction } from "react";
import { useEffect, useMemo, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { addDefaultEntry } from "store/priceHistory/priceHistoryStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { selectTitleDefaultPrice } from "store/titleStore";
import { ChannelCalculationMethod } from "store/types";

import ChannelContent from "../ChannelContent";
import SubscriptionChannelContent from "../SubscriptionChannelContent";

export type TFilteredCountryMarketData = {
    countryMarketByChannelId: Record<number, string[]>;
    fieldControlName: Record<string, string>;
};

interface ISpecificChannelsForm {
    onCloseNewChannelModal: () => void;
    onOpenNewChannelModal: boolean;
    /**
     * Current active channels (toggle on)
     */
    activeChannels: number[];
    setActiveChannels: React.Dispatch<SetStateAction<number[]>>;
    /**
     * current data title
     */
    dataTitle: ITitle | null;
}

export const SpecificChannelsForm = ({
    onCloseNewChannelModal,
    onOpenNewChannelModal,
    activeChannels,
    setActiveChannels,
    dataTitle,
}: ISpecificChannelsForm) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const {
        formState: { errors },
        control,
        watch,
        clearErrors,
        setValue,
    } = useFormContext<TFormCreateTitle>();

    const { fields, append } = useFieldArray({
        control,
        name: "overriddenChannelsData",
    });

    const { companyCurrency } = useCompanySettings();
    const [foldedChannels, setFoldedChannels] = useState<Set<number>>(new Set());
    useEffect(() => {
        setFoldedChannels(new Set(fields.filter((f) => !activeChannels.includes(f.channelId)).map((f) => f.channelId)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeChannels, fields]);

    const toggleFold = (channelId: number) => {
        if (!activeChannels.some((item) => item === channelId)) return;
        setFoldedChannels((prev) => {
            const next = new Set(prev);
            if (next.has(channelId)) next.delete(channelId);
            else next.add(channelId);
            return next;
        });
    };

    const defaultTitlePrice = useAppSelector(selectTitleDefaultPrice);
    const persistedChannelById = useMemo(() => {
        const byId = new Map<number, NonNullable<ITitle["overriddenChannelsData"]>[number]>();
        for (const channel of dataTitle?.overriddenChannelsData ?? []) {
            if (channel.countryMarket) continue;
            byId.set(channel.channelId, channel);
        }
        return byId;
    }, [dataTitle?.overriddenChannelsData]);

    /**
     * Added channels is used to have the updated option list while choosing to add a channel
     */
    const addedChannels = fields.map((channel) => channel.channelId);

    const handleActiveChannel = (e: React.ChangeEvent<HTMLInputElement>, index: number, channelId?: number) => {
        const channel = watch(`overriddenChannelsData.${index}`);
        if (channelId) {
            if (e.target.checked) {
                setActiveChannels((prevChannels) => [...prevChannels, channelId]);
                setFoldedChannels((prev) => new Set([...prev].filter((e) => e !== channelId)));
                if (
                    channel.calculationMethod === ChannelCalculationMethod.SUBSCRIPTION &&
                    !channel.subscriptionCoefficient
                ) {
                    setValue(`overriddenChannelsData.${index}.subscriptionCoefficient`, 1);
                }
                // Add default price history entry for this channel if not already present
                if (featureFlags.priceHistory && channel.calculationMethod !== ChannelCalculationMethod.SUBSCRIPTION) {
                    dispatch(
                        addDefaultEntry({
                            channelId,
                            titleId: dataTitle?.id,
                            price: defaultTitlePrice,
                            currency: companyCurrency,
                        }),
                    );
                }
            }
            if (!e.target.checked) {
                setActiveChannels((prevChannels) => prevChannels.filter((item) => item !== channelId));
                setFoldedChannels((prev) => new Set([...prev, channelId]));
            }
            setValue(`overriddenChannelsData.${index}.releaseDate`, undefined, {
                shouldDirty: true,
            }); // shouldDirty trigger edit to show validate modif btn
            setValue("shallRecompute", true, { shouldDirty: true });
            clearErrors([`overriddenChannelsData.${index}.ean13`, `overriddenChannelsData.${index}.priceHistory`]);
        }
    };

    return (
        <>
            <ModalBox
                onClose={onCloseNewChannelModal}
                open={onOpenNewChannelModal}
                mainTitle={t("NewTitle.AddNewChannel")}
            >
                <AddSpecificChannel
                    close={onCloseNewChannelModal}
                    addedChannels={addedChannels}
                    setActiveChannels={setActiveChannels}
                    append={append}
                />
            </ModalBox>

            {fields.map((channel, index) => {
                const persistedChannel = persistedChannelById.get(channel.channelId);
                const showName = hasLegacyChannelName(persistedChannel?.name);
                const showEan13 = hasLegacyEan13(persistedChannel?.ean13);
                const showReleaseDate = hasLegacyReleaseDate(persistedChannel?.releaseDate);
                const onActiveChannel = activeChannels.some((item) => item === channel?.channelId);
                const isFolded = foldedChannels.has(channel.channelId);

                // only render primary channels (that doesnt have country market)
                if (!channel.countryMarket)
                    return (
                        <div
                            className="border border-gray-200 rounded-[3px] overflow-hidden"
                            key={`channel-${channel?.id}`}
                        >
                            {/* Header */}
                            <div
                                className={classNames(
                                    `flex items-center justify-between px-4 py-3`,
                                    onActiveChannel && "cursor-pointer",
                                )}
                                onClick={() => toggleFold(channel.channelId)}
                            >
                                <div
                                    className="flex items-center gap-2 transition-opacity duration-300"
                                    style={{ opacity: onActiveChannel ? 1 : 0.6 }}
                                >
                                    {isFolded ? (
                                        <KeyboardArrowRightIcon fontSize="small" />
                                    ) : (
                                        <KeyboardArrowDownIcon fontSize="small" />
                                    )}
                                    <ChannelIcon channelKind={channel?.kind as number} color="#141516" />
                                    <span className="font-bold text-sm">{channel?.channelName}</span>
                                </div>
                                <div
                                    className="flex items-center gap-2"
                                    id={`switch-${index}`}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {!onActiveChannel && dataTitle && dataTitle.overriddenChannelsData?.length > 0 ? (
                                        <div className="channel-reminder">
                                            <div className="chip" />
                                            {t("NewTitle.ChannelWillBeDeleted")}
                                        </div>
                                    ) : (
                                        onActiveChannel && (
                                            <span className="text-sm text-gray-500">{t("NewTitle.Active")}</span>
                                        )
                                    )}
                                    <LargeSwitch
                                        onChange={(e: any) => handleActiveChannel(e, index, channel?.channelId)}
                                        checked={onActiveChannel}
                                    />
                                </div>
                            </div>

                            {/* Content */}
                            {!isFolded && (
                                <div
                                    className={classNames(
                                        "border-t border-gray-200",
                                        !onActiveChannel && "opacity-55 select-none pointer-events-none",
                                    )}
                                >
                                    <div className="p-4 flex flex-col gap-4">
                                        {channel.calculationMethod === ChannelCalculationMethod.SUBSCRIPTION ? (
                                            <SubscriptionChannelContent
                                                index={index}
                                                useFormContextData={{
                                                    control: control,
                                                    errors: errors,
                                                    watch: watch,
                                                }}
                                                controlName={{
                                                    name: `overriddenChannelsData.${index}.name`,
                                                    releaseDate: `overriddenChannelsData.${index}.releaseDate`,
                                                    ean13: `overriddenChannelsData.${index}.ean13`,
                                                    subscriptionCoefficient: `overriddenChannelsData.${index}.subscriptionCoefficient`,
                                                }}
                                                showName={showName}
                                                showEan13={showEan13}
                                                showReleaseDate={showReleaseDate}
                                            />
                                        ) : (
                                            <ChannelContent
                                                index={index}
                                                useFormContextData={{
                                                    control: control,
                                                    errors: errors,
                                                    watch: watch,
                                                }}
                                                controlName={{
                                                    name: `overriddenChannelsData.${index}.name`,
                                                    releaseDate: `overriddenChannelsData.${index}.releaseDate`,
                                                    ean13: `overriddenChannelsData.${index}.ean13`,
                                                }}
                                                showName={showName}
                                                showEan13={showEan13}
                                                showReleaseDate={showReleaseDate}
                                            />
                                        )}
                                        {featureFlags.priceHistory &&
                                            channel.calculationMethod !== ChannelCalculationMethod.SUBSCRIPTION && (
                                                <ChannelPriceHistory titleId={dataTitle?.id} channel={channel} isEdit />
                                            )}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
            })}
        </>
    );
};
